import { pct } from './format.js';

// How this mix has behaved when things went wrong.
//
// Deliberately risk-only. Trailing returns were here briefly and were cut on
// the advisor lead's objection, which deserves recording because it is a
// product decision, not a style one: a prospect shown a return figure draws a
// conclusion from it ("I'm beating the market, why change?"), and that
// conclusion is usually wrong in a way that costs the meeting. The backend
// still fetches the returns, so an advisor-facing view can surface them later
// without a data change; the client-facing page shows what the mix did at its
// worst, which is the figure that changes decisions.
//
// Renders nothing at all until the numbers exist. A blank tile or a zero would
// both read as "flat", which is a lie about performance rather than an absence
// of data.

export default function BehaviourSection({ calc, poll }) {
  if (!calc) return null;

  const stats = poll?.stats;
  const ready = poll?.status === 'ready' || poll?.status === 'needs_review';

  if (!ready) {
    // Deliberately quiet, and never in print: the rest of the report is
    // complete and correct without this, so a spinner must not imply otherwise.
    if (poll?.status === 'calculating') {
      return (
        <p className="asmt-calc-note no-print">
          Performance and risk figures for this mix are still being calculated.
          Everything above is complete.
        </p>
      );
    }
    return null;
  }

  const k = stats.riskStats || {};
  const drawdown = Number.isFinite(k.maxDrawdown5y) ? Math.abs(k.maxDrawdown5y) : null;
  const hasAny = [k.stdDev5y, drawdown].some(Number.isFinite);
  if (!hasAny) return null;

  return (
    <section className="asmt-card" id="behaviour">
      <div className="asmt-card-head">
        <h2>How this mix behaves when markets fall</h2>
        <span className="asmt-card-note">your holdings, priced through history</span>
      </div>
      <p className="asmt-lede">
        History, not a forecast. The average year tells you very little; the worst
        stretch tells you what this portfolio asks of your nerve, and whether the
        plan survives you living through it.
      </p>

      {Number.isFinite(drawdown) && (
        <div className="asmt-callout">
          <strong>At its worst, this mix fell {pct(drawdown, 1)}.</strong>{' '}
          That is the largest drop from a peak in the last five years. The question
          worth asking is not whether it recovered, but whether you would have stayed
          invested while it happened.
        </div>
      )}

      <div className="asmt-tiles">
        {Number.isFinite(drawdown) && (
          <div className="asmt-tile">
            <div className="asmt-tile-label">Largest fall, five years</div>
            <div className="asmt-tile-value is-gap">{pct(drawdown, 1)}</div>
            <div className="asmt-tile-note">peak to trough</div>
          </div>
        )}
        {Number.isFinite(k.stdDev5y) && (
          <div className="asmt-tile">
            <div className="asmt-tile-label">Volatility</div>
            <div className="asmt-tile-value">{pct(k.stdDev5y, 1)}</div>
            <div className="asmt-tile-note">how much it moves in a typical year</div>
          </div>
        )}
      </div>

      {poll.status === 'needs_review' && (
        <p className="asmt-note" style={{ marginTop: 14 }}>
          Some positions could not be priced across the whole period, so these
          figures cover part of the portfolio rather than all of it.
        </p>
      )}
    </section>
  );
}
